import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { Edit3, Plus, Save, Search, Trash2 } from "lucide-react-native";
import { api } from "../api/client";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, ScreenHeader, Toolbar } from "../components/UI";
import { styles } from "../styles";

const empty = { company_name: "", contact_person: "", mobile: "", email: "", address: "", city: "", ntn_strn: "", notes: "" };

export function ClientsScreen({ user }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const isAdmin = user && String(user.role || "").toLowerCase().includes("admin");

  const load = () => api.clients(q).then(setRows).catch((error) => Alert.alert("Clients", error.message));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.company_name?.trim()) {
      return Alert.alert("Clients", "Company Name is required.");
    }
    try {
      await api.saveClient(form);
      setForm(empty);
      load();
    } catch (error) {
      Alert.alert("Clients", error.message);
    }
  };

  const remove = async (id, name) => {
    const choice = prompt(`Delete client "${name || 'selected'}"?\nType 'permanent' to permanently erase, or click OK to deactivate:`, "deactivate");
    if (choice === null) return;
    const isPermanent = String(choice).trim().toLowerCase() === "permanent";
    try {
      await api.deleteClient(id, isPermanent);
      load();
    } catch (error) {
      Alert.alert("Clients", error.message);
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader title="Client Management" subtitle="Add, edit, delete, and search client records (Global and Account-specific)" />

      <Card>
        <Text style={styles.h2}>{form.id ? "Edit Client Profile" : "Add New Client"}</Text>
        <View style={styles.row}>
          <Field label="Company Name *" value={form.company_name} onChangeText={(v) => setForm({ ...form, company_name: v })} style={{ flex: 2 }} />
          <Field label="Contact Person" value={form.contact_person} onChangeText={(v) => setForm({ ...form, contact_person: v })} />
          <Field label="Mobile / Phone" value={form.mobile} onChangeText={(v) => setForm({ ...form, mobile: v })} />
          <Field label="Email Address" value={form.email} onChangeText={(v) => setForm({ ...form, email: v })} />
        </View>

        <View style={styles.row}>
          <Field label="City" value={form.city} onChangeText={(v) => setForm({ ...form, city: v })} />
          <Field label="NTN / STRN Registration" value={form.ntn_strn} onChangeText={(v) => setForm({ ...form, ntn_strn: v })} />
          <Field label="Address" value={form.address} onChangeText={(v) => setForm({ ...form, address: v })} multiline style={{ flex: 2 }} />
          <Field label="Notes" value={form.notes} onChangeText={(v) => setForm({ ...form, notes: v })} multiline style={{ flex: 2 }} />
        </View>

        <Toolbar>
          <Button icon={Save} onPress={save}>{form.id ? "Update Client Profile" : "Save Client Profile"}</Button>
          <Button icon={Plus} variant="secondary" onPress={() => setForm(empty)}>Clear / New</Button>
        </Toolbar>
      </Card>

      <Card style={{ padding: 16 }}>
        <Toolbar>
          <Field label="Search Clients Directory" value={q} onChangeText={setQ} placeholder="Company name, contact person, email, city..." style={{ flex: 2 }} />
          <Button icon={Search} onPress={load}>Search Clients</Button>
        </Toolbar>
      </Card>

      <DataTable
        actionsWidth={56}
        columns={[
          { key: "company_name", label: "Company Name", flex: 1.8, style: { fontWeight: "700" } },
          { key: "contact_person", label: "Contact Person", flex: 1.3 },
          { key: "mobile", label: "Mobile", flex: 1.1 },
          { key: "email", label: "Email", flex: 1.4 },
          { key: "city", label: "City", flex: 0.9 },
          { key: "ntn_strn", label: "NTN / STRN", flex: 0.9 },
          {
            key: "scope",
            label: "Scope",
            flex: 0.9,
            render: (r) => (
              <Badge variant={!r.created_by_user_id ? "secondary" : "info"}>
                {!r.created_by_user_id ? "GLOBAL" : (Number(r.created_by_user_id) === Number(user?.id) ? "MY ACCOUNT" : "ACCOUNT")}
              </Badge>
            )
          }
        ]}
        rows={rows}
        renderActions={(row) => {
          const isOwner = Number(row.created_by_user_id) === Number(user?.id);
          const canManage = isAdmin || isOwner;
          if (!canManage) return null;
          return (
            <ActionMenu
              actions={[
                { label: "Edit Client Profile", icon: Edit3, onPress: () => setForm(row) },
                { label: "Delete Client", icon: Trash2, variant: "danger", onPress: () => remove(row.id, row.company_name) }
              ]}
            />
          );
        }}
      />
    </View>
  );
}
